import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { formatLiveTime } from '../../utils/dateUtils';

const Container = styled.div`
  background-color: #f3f6f6;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

// 라이브 배지 (작은 빨간 원)
const LiveBadge = styled.div`
  background-color: #ed2424;
  border-radius: 50%;
  width: 6px;
  height: 6px;
  flex-shrink: 0;
`;

// 라이브 텍스트
const LiveText = styled.span`
  font-size: 12px;
  color: #262626;
  font-weight: 600;
`;

// 라이브 배지 컨테이너 (원형 + 텍스트)
const LiveBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 라이브 시작 시간 텍스트
const LiveStartTime = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

// 라이브 타이머 컨테이너 (라이브 중일 때)
const LiveTimerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 타이머 박스 (검정색 배경)
const TimerBox = styled.div`
  background-color: #262626;
  color: white;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  width: 40px;
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 타이머 단위 텍스트
const TimerUnit = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 500;
`;

// 남은 시간 텍스트
const RemainingText = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 500;
`;

interface LiveStartTimeInfoProps {
  liveStartTime: string;
  liveEndTime?: string;
}

// 다국어 텍스트
const liveStartTimeTexts = {
  ko: {
    live: 'LIVE',
    end: 'END',
    ended: '경매가 종료되었습니다',
    remaining: '남음'
  },
  en: {
    live: 'LIVE',
    end: 'END',
    ended: 'Auction has ended',
    remaining: 'remaining'
  }
};

// 라이브 상태 확인 함수
const getLiveStatus = (startTime: string, endTime?: string): 'before' | 'starting-soon' | 'live' | 'ended' => {
  const now = new Date();
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : null;
  const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  if (end && now > end) return 'ended';
  if (now >= start) return 'live';
  if (nowDate.getTime() === startDate.getTime()) return 'starting-soon'; // 당일
  return 'before'; // 전날 이전
};

// 남은 시간 계산 (xxh xxm 형태)
const calculateRemainingTime = (targetTime: string): { hours: number; minutes: number } => {
  const now = new Date();
  const target = new Date(targetTime);
  const diff = target.getTime() - now.getTime();
  
  if (diff <= 0) return { hours: 0, minutes: 0 };
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { hours, minutes };
};

const LiveStartTimeInfo: React.FC<LiveStartTimeInfoProps> = ({
  liveStartTime,
  liveEndTime
}) => {
  const { lang } = useLang();
  const texts = liveStartTimeTexts[lang];
  const [liveStatus, setLiveStatus] = useState<'before' | 'starting-soon' | 'live' | 'ended'>('before');
  const [remainingTime, setRemainingTime] = useState<{ hours: number; minutes: number }>({ hours: 0, minutes: 0 });

  useEffect(() => {
    const updateLiveInfo = () => {
      if (liveStartTime) {
        const status = getLiveStatus(liveStartTime, liveEndTime);
        setLiveStatus(status);
        
        if (status === 'starting-soon') {
          setRemainingTime(calculateRemainingTime(liveStartTime));
        } else if (status === 'live' && liveEndTime) {
          setRemainingTime(calculateRemainingTime(liveEndTime));
        }
      }
    };
    
    updateLiveInfo();

    // 1분마다 시간 업데이트
    const interval = setInterval(updateLiveInfo, 60000);

    return () => clearInterval(interval);
  }, [liveStartTime, liveEndTime]);

  if (!liveStartTime) {
    return null;
  }

  const renderLiveInfo = () => {
    switch (liveStatus) {
      case 'before':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge />
              <LiveText>{texts.live}</LiveText>
            </LiveBadgeContainer>
            <LiveStartTime>{formatLiveTime(liveStartTime, lang)}</LiveStartTime>
          </>
        );
      
      case 'starting-soon':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge />
              <LiveText>{texts.live}</LiveText>
            </LiveBadgeContainer>
            <LiveStartTime>{remainingTime.hours}h {remainingTime.minutes}m</LiveStartTime>
          </>
        );
      
      case 'live':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge />
              <LiveText>{texts.live}</LiveText>
            </LiveBadgeContainer>
            <LiveTimerContainer>
              <TimerBox>{remainingTime.hours}</TimerBox>
              <TimerUnit>h</TimerUnit>
              <TimerBox>{remainingTime.minutes}</TimerBox>
              <TimerUnit>m</TimerUnit>
              <RemainingText>{texts.remaining}</RemainingText>
            </LiveTimerContainer>
          </>
        );
      
      case 'ended':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge style={{ backgroundColor: '#999' }} />
              <LiveText>{texts.end}</LiveText>
            </LiveBadgeContainer>
            <LiveStartTime>{texts.ended}</LiveStartTime>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container>
      {renderLiveInfo()}
    </Container>
  );
};

export default LiveStartTimeInfo;
